import { envConfigs } from '../../../envConfigs';
import { specialPasswordChars } from '../../login/services/password-change-helpers';
import { handleServiceCall } from './fetch-utils';
import { isValidEmail } from './email-helper';

export const getUserInfo = (userId, token) => {
  const egoUrl = envConfigs.egoApiHost + '/api/users/' + userId;
  return fetch(egoUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-store, no-cache, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Accept: 'application/json',
      Authorization: 'Bearer ' + token
    }
  }).then((response) => {
    return handleServiceCall(response).then((userInfo) => {
      return userInfo;
    });
  });
};

// NOTE: strict passwords are:
//  using english alpha letters
//  between 12-30 characters in length
//  at least one uppercase alpha character
//  at least one digit
//  at least one special character
const validateStrictPassword = (password) => {
  if (password.length < 12) {
    return 'error_password_too_short_strict';
  }
  if (password.length > 30) {
    return 'error_password_too_long';
  }
  let hasOneDigit = false;
  let hasOneUppercase = false;
  let hasOneSpecialChar = false;
  for (let x = 0; x < password.length; x++) {
    const char = password.charAt(x);
    if ('0123456789'.includes(char)) {
      hasOneDigit = true;
    } else if ('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.includes(char)) {
      hasOneUppercase = true;
    } else if (specialPasswordChars.includes(char)) {
      hasOneSpecialChar = true;
    } else if (!'abcdefghijklmnopqrstuvwxyz'.includes(char)) {
      return 'error_password_illegal_char';
    }
  }
  if (!hasOneDigit) {
    return 'error_password_needs_digit';
  }
  if (!hasOneUppercase) {
    return 'error_password_needs_upper';
  }
  if (!hasOneSpecialChar) {
    return 'error_password_needs_special';
  }
  return '';
};


// NOTE: this strict password checking is for new Consolidated login portal
//  using english alpha letters
//  at lease 8 characters in length
//  at least one uppercase alpha character
//  at least one lowercase alpha character
//  at least one digit
//  at least one special character
const validateStrictPasswordNew = (password) => {
  let isTooShort = false;
  if (password.length < 8) {
    isTooShort = true;
  }
  let hasOneDigit = false;
  let hasOneUppercase = false;
  let hasOneLowercase = false;
  let hasOneSpecialChar = false;
  let hasIllegalChar = false;
  for (let x = 0; x < password.length; x++) {
    const char = password.charAt(x);
    if ('0123456789'.includes(char)) {
      hasOneDigit = true;
    } else if ('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.includes(char)) {
      hasOneUppercase = true;
    } else if ('abcdefghijklmnopqrstuvwxyz'.includes(char)) {
      hasOneLowercase = true;
    } else if (specialPasswordChars.includes(char)) {
      hasOneSpecialChar = true;
    } else {
      hasIllegalChar = true;
    }
  }

  if (!isTooShort && hasOneDigit && hasOneUppercase && hasOneLowercase && hasOneSpecialChar && !hasIllegalChar) {
    return '';
  }
  return [isTooShort, hasOneDigit, hasOneUppercase, hasOneLowercase, hasOneSpecialChar, hasIllegalChar]
};

/*
  for register and reset password and profile
    hand in a field name and its value, and (for passwords that must match)
    another value to validate against
    get back an error to display or '' if all valid
 */
export const validateUserInfo = (fieldName, fieldValue, passwordToMatch, passwordMode) => {
  switch (fieldName) {
    case 'email':
      if (!fieldValue) {
        return 'error_email_field_empty';
      }
      if (!isValidEmail(fieldValue)) {
        return 'invalid_email';
      }
      break;
    case 'password':
      if (!fieldValue) {
        return 'error_password_field_empty';
      }
      break;
    case 'newPassword':
      if (!fieldValue) {
        return 'error_password_field_empty';
      }
      if (passwordMode === 'strict') {
        return validateStrictPassword(fieldValue);
      } else if (passwordMode === 'strict-new') {
        return validateStrictPasswordNew(fieldValue);
      } else {
        // non-strict password rules:
        if (fieldValue.length < 6) {
          return 'error_password_too_short';
        }
      }
      break;
    case 'newPasswordMatch':
      if (!fieldValue) {
        return 'error_password_field_empty';
      }
      if (fieldValue !== passwordToMatch) {
        return 'error_passwords_dont_match';
      }
      break;
    case 'timezone':
      if (!fieldValue) {
        return 'profile_no_timezone';
      }
      break;
    case 'terms':
      if (!fieldValue) {
        return 'error_terms_of_use_not_checked';
      }
      break;
    case 'voiceType':
      if (!fieldValue) {
        return 'profile_no_voice_type';
      }
      break;
    default:
  }
  return '';
};

export const voiceTypes = [
  { labelKey: 'profile_voice_type_adult_female', value: 'female' },
  { labelKey: 'profile_voice_type_male', value: 'male' }
];

export const timezones = [
  { labelKey: '_timezone_pacific_midway', value: 'Pacific/Midway' },
  { labelKey: '_timezone_pacific_pago_pago', value: 'Pacific/Pago_Pago' },
  { labelKey: '_timezone_pacific_honolulu', value: 'Pacific/Honolulu' },
  { labelKey: '_timezone_america_juneau', value: 'America/Juneau' },
  { labelKey: '_timezone_america_los_angeles', value: 'America/Los_Angeles' },
  { labelKey: '_timezone_america_tijuana', value: 'America/Tijuana' },
  { labelKey: '_timezone_america_phoenix', value: 'America/Phoenix' },
  { labelKey: '_timezone_america_chihuahua', value: 'America/Chihuahua' },
  { labelKey: '_timezone_america_mazatlan', value: 'America/Mazatlan' },
  { labelKey: '_timezone_america_denver', value: 'America/Denver' },
  { labelKey: '_timezone_america_guatemala', value: 'America/Guatemala' },
  { labelKey: '_timezone_america_chicago', value: 'America/Chicago' },
  { labelKey: '_timezone_america_mexico_city', value: 'America/Mexico_City' },
  { labelKey: '_timezone_america_monterrey', value: 'America/Monterrey' },
  { labelKey: '_timezone_america_regina', value: 'America/Regina' },
  { labelKey: '_timezone_america_bogota', value: 'America/Bogota' },
  { labelKey: '_timezone_america_new_york', value: 'America/New_York' },
  { labelKey: '_timezone_america_indiana/indianapolis', value: 'America/Indiana/Indianapolis' },
  { labelKey: '_timezone_america_lima', value: 'America/Lima' },
  { labelKey: '_timezone_america_caracas', value: 'America/Caracas' },
  { labelKey: '_timezone_america_halifax', value: 'America/Halifax' },
  { labelKey: '_timezone_america_guyana', value: 'America/Guyana' },
  { labelKey: '_timezone_america_la_paz', value: 'America/La_Paz' },
  { labelKey: '_timezone_america_santiago', value: 'America/Santiago' },
  { labelKey: '_timezone_america_st_johns', value: 'America/St_Johns' },
  { labelKey: '_timezone_america_sao_paulo', value: 'America/Sao_Paulo' },
  { labelKey: '_timezone_america_argentina/buenos_aires', value: 'America/Argentina/Buenos_Aires' },
  { labelKey: '_timezone_america_godthab', value: 'America/Godthab' },
  { labelKey: '_timezone_atlantic_south_georgia', value: 'Atlantic/South_Georgia' },
  { labelKey: '_timezone_atlantic_azores', value: 'Atlantic/Azores' },
  { labelKey: '_timezone_atlantic_cape_verde', value: 'Atlantic/Cape_Verde' },
  { labelKey: '_timezone_africa_casablanca', value: 'Africa/Casablanca' },
  { labelKey: '_timezone_europe_dublin', value: 'Europe/Dublin' },
  { labelKey: '_timezone_europe_lisbon', value: 'Europe/Lisbon' },
  { labelKey: '_timezone_europe_london', value: 'Europe/London' },
  { labelKey: '_timezone_africa_monrovia', value: 'Africa/Monrovia' },
  { labelKey: '_timezone_etc_utc', value: 'Etc/UTC' },
  { labelKey: '_timezone_europe_amsterdam', value: 'Europe/Amsterdam' },
  { labelKey: '_timezone_europe_belgrade', value: 'Europe/Belgrade' },
  { labelKey: '_timezone_europe_berlin', value: 'Europe/Berlin' },
  { labelKey: '_timezone_europe_zurich', value: 'Europe/Zurich' },
  { labelKey: '_timezone_europe_bratislava', value: 'Europe/Bratislava' },
  { labelKey: '_timezone_europe_brussels', value: 'Europe/Brussels' },
  { labelKey: '_timezone_europe_budapest', value: 'Europe/Budapest' },
  { labelKey: '_timezone_europe_copenhagen', value: 'Europe/Copenhagen' },
  { labelKey: '_timezone_europe_ljubljana', value: 'Europe/Ljubljana' },
  { labelKey: '_timezone_europe_madrid', value: 'Europe/Madrid' },
  { labelKey: '_timezone_europe_paris', value: 'Europe/Paris' },
  { labelKey: '_timezone_europe_prague', value: 'Europe/Prague' },
  { labelKey: '_timezone_europe_rome', value: 'Europe/Rome' },
  { labelKey: '_timezone_europe_sarajevo', value: 'Europe/Sarajevo' },
  { labelKey: '_timezone_europe_skopje', value: 'Europe/Skopje' },
  { labelKey: '_timezone_europe_stockholm', value: 'Europe/Stockholm' },
  { labelKey: '_timezone_europe_vienna', value: 'Europe/Vienna' },
  { labelKey: '_timezone_europe_warsaw', value: 'Europe/Warsaw' },
  { labelKey: '_timezone_africa_algiers', value: 'Africa/Algiers' },
  { labelKey: '_timezone_europe_zagreb', value: 'Europe/Zagreb' },
  { labelKey: '_timezone_europe_athens', value: 'Europe/Athens' },
  { labelKey: '_timezone_europe_bucharest', value: 'Europe/Bucharest' },
  { labelKey: '_timezone_africa_cairo', value: 'Africa/Cairo' },
  { labelKey: '_timezone_africa_harare', value: 'Africa/Harare' },
  { labelKey: '_timezone_europe_helsinki', value: 'Europe/Helsinki' },
  { labelKey: '_timezone_europe_istanbul', value: 'Europe/Istanbul' },
  { labelKey: '_timezone_asia_jerusalem', value: 'Asia/Jerusalem' },
  { labelKey: '_timezone_europe_kiev', value: 'Europe/Kiev' },
  { labelKey: '_timezone_africa_johannesburg', value: 'Africa/Johannesburg' },
  { labelKey: '_timezone_europe_riga', value: 'Europe/Riga' },
  { labelKey: '_timezone_europe_sofia', value: 'Europe/Sofia' },
  { labelKey: '_timezone_europe_tallinn', value: 'Europe/Tallinn' },
  { labelKey: '_timezone_europe_vilnius', value: 'Europe/Vilnius' },
  { labelKey: '_timezone_asia_baghdad', value: 'Asia/Baghdad' },
  { labelKey: '_timezone_asia_kuwait', value: 'Asia/Kuwait' },
  { labelKey: '_timezone_europe_minsk', value: 'Europe/Minsk' },
  { labelKey: '_timezone_africa_nairobi', value: 'Africa/Nairobi' },
  { labelKey: '_timezone_asia_riyadh', value: 'Asia/Riyadh' },
  { labelKey: '_timezone_asia_tehran', value: 'Asia/Tehran' },
  { labelKey: '_timezone_asia_muscat', value: 'Asia/Muscat' },
  { labelKey: '_timezone_asia_baku', value: 'Asia/Baku' },
  { labelKey: '_timezone_europe_moscow', value: 'Europe/Moscow' },
  { labelKey: '_timezone_asia_tbilisi', value: 'Asia/Tbilisi' },
  { labelKey: '_timezone_asia_yerevan', value: 'Asia/Yerevan' },
  { labelKey: '_timezone_asia_kabul', value: 'Asia/Kabul' },
  { labelKey: '_timezone_asia_karachi', value: 'Asia/Karachi' },
  { labelKey: '_timezone_asia_tashkent', value: 'Asia/Tashkent' },
  { labelKey: '_timezone_asia_kolkata', value: 'Asia/Kolkata' },
  { labelKey: '_timezone_asia_colombo', value: 'Asia/Colombo' },
  { labelKey: '_timezone_asia_kathmandu', value: 'Asia/Kathmandu' },
  { labelKey: '_timezone_asia_almaty', value: 'Asia/Almaty' },
  { labelKey: '_timezone_asia_dhaka', value: 'Asia/Dhaka' },
  { labelKey: '_timezone_asia_yekaterinburg', value: 'Asia/Yekaterinburg' },
  { labelKey: '_timezone_asia_rangoon', value: 'Asia/Rangoon' },
  { labelKey: '_timezone_asia_bangkok', value: 'Asia/Bangkok' },
  { labelKey: '_timezone_asia_jakarta', value: 'Asia/Jakarta' },
  { labelKey: '_timezone_asia_shanghai', value: 'Asia/Shanghai' },
  { labelKey: '_timezone_asia_chongqing', value: 'Asia/Chongqing' },
  { labelKey: '_timezone_asia_hong_kong', value: 'Asia/Hong_Kong' },
  { labelKey: '_timezone_asia_krasnoyarsk', value: 'Asia/Krasnoyarsk' },
  { labelKey: '_timezone_asia_kuala_lumpur', value: 'Asia/Kuala_Lumpur' },
  { labelKey: '_timezone_australia_perth', value: 'Australia/Perth' },
  { labelKey: '_timezone_asia_singapore', value: 'Asia/Singapore' },
  { labelKey: '_timezone_asia_taipei', value: 'Asia/Taipei' },
  { labelKey: '_timezone_asia_ulaanbaatar', value: 'Asia/Ulaanbaatar' },
  { labelKey: '_timezone_asia_urumqi', value: 'Asia/Urumqi' },
  { labelKey: '_timezone_asia_irkutsk', value: 'Asia/Irkutsk' },
  { labelKey: '_timezone_asia_seoul', value: 'Asia/Seoul' },
  { labelKey: '_timezone_asia_tokyo', value: 'Asia/Tokyo' },
  { labelKey: '_timezone_australia_adelaide', value: 'Australia_Adelaide' },
  { labelKey: '_timezone_australia_darwin', value: 'Australia/Darwin' },
  { labelKey: '_timezone_australia_brisbane', value: 'Australia/Brisbane' },
  { labelKey: '_timezone_pacific_guam', value: 'Pacific/Guam' },
  { labelKey: '_timezone_australia_hobart', value: 'Australia/Hobart' },
  { labelKey: '_timezone_australia_melbourne', value: 'Australia/Melbourne' },
  { labelKey: '_timezone_pacific_port_moresby', value: 'Pacific/Port_Moresby' },
  { labelKey: '_timezone_australia_sydney', value: 'Australia/Sydney' },
  { labelKey: '_timezone_asia_yakutsk', value: 'Asia/Yakutsk' },
  { labelKey: '_timezone_pacific_noumea', value: 'Pacific/Noumea' },
  { labelKey: '_timezone_asia_vladivostok', value: 'Asia/Vladivostok' },
  { labelKey: '_timezone_pacific_auckland', value: 'Pacific/Auckland' },
  { labelKey: '_timezone_pacific_fiji', value: 'Pacific/Fiji' },
  { labelKey: '_timezone_asia_kamchatka', value: 'Asia/Kamchatka' },
  { labelKey: '_timezone_asia_magadan', value: 'Asia/Magadan' },
  { labelKey: '_timezone_pacific_majuro', value: 'Pacific/Majuro' },
  { labelKey: '_timezone_pacific_tongatapu', value: 'Pacific/Tongatapu' },
  { labelKey: '_timezone_pacific_apia', value: 'Pacific/Apia' }
];

/*
  Used to determine if user was given a license only for the purpose of
  taking the Placement Test. These users should not have access to any
  learning products.
 */
export const isUserCatalystPreTestOnly = (userInfo) => {
  const PLACEMENT_TEST_ONLY_IDENTIFIER = 'CatalystPreTestOnly';

  return (
    userInfo.userField1 === PLACEMENT_TEST_ONLY_IDENTIFIER ||
    userInfo.userField2 === PLACEMENT_TEST_ONLY_IDENTIFIER ||
    userInfo.userField3 === PLACEMENT_TEST_ONLY_IDENTIFIER ||
    userInfo.userField4 === PLACEMENT_TEST_ONLY_IDENTIFIER ||
    userInfo.userField5 === PLACEMENT_TEST_ONLY_IDENTIFIER ||
    userInfo.userField6 === PLACEMENT_TEST_ONLY_IDENTIFIER
  );
};

export const getTargetLanguageCode = (userInfo) => {
  return userInfo && userInfo.targetLanguage && userInfo.targetLanguage.language
    ? userInfo.targetLanguage.language
    : '';
};



// WEBPACK FOOTER //
// ./src/modules/app/util/user-helper.js